import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogContent, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { postVisual } from "../api";
import { VisualImage } from "../types";
import { useToast } from "../../../components/Toast";

interface DistantScreenToolProps {
  patientId: string;
  sessionId?: string;
  getToken: () => Promise<string | null>;
}

/* Imaginal distancing: the scene rendered near → mid → far, stepped through
   as the client watches it recede. Image ids come back as near/mid/far. */
export function DistantScreenTool({ patientId, sessionId, getToken }: DistantScreenToolProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [scene, setScene] = useState("");
  const [busy, setBusy] = useState(false);
  const [steps, setSteps] = useState<VisualImage[]>([]);
  const [stepIdx, setStepIdx] = useState(0);
  const [presentOpen, setPresentOpen] = useState(false);

  const handleGenerate = async () => {
    try {
      setBusy(true);
      const res = await postVisual(getToken, {
        patient_id: patientId, session_id: sessionId, kind: "distance", prompt: scene,
      });
      setSteps(res.images);
      setStepIdx(0);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg.includes("image_policy_declined") ? t("experiential.policyDeclined") : t("experiential.genError"));
    } finally {
      setBusy(false);
    }
  };

  const current = steps[stepIdx];

  return (
    <div>
      <TextField
        label={t("experiential.distScene")}
        multiline
        minRows={3}
        fullWidth
        size="small"
        value={scene}
        onChange={(e) => setScene(e.target.value)}
        sx={{ mb: 2 }}
      />
      <Button variant="contained" size="small" onClick={handleGenerate} disabled={busy || !scene.trim()} sx={{ mb: 2 }}>
        {t("experiential.generateSeries")}
      </Button>

      {steps.length > 0 && (
        <>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={stepIdx}
            onChange={(_e, v) => { if (v !== null) setStepIdx(v); }}
            sx={{ mb: 2, display: "flex" }}
          >
            {steps.map((s, i) => (
              <ToggleButton key={s.id} value={i} sx={{ flex: 1 }}>
                {t(`experiential.distSteps.${s.id}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <img src={current.url} alt={t(`experiential.distSteps.${current.id}`)} style={{ width: "100%", borderRadius: 6 }} />

          <Button variant="outlined" size="small" onClick={() => setPresentOpen(true)} sx={{ mt: 2 }}>
            {t("experiential.present")}
          </Button>
          <Dialog open={presentOpen} onClose={() => setPresentOpen(false)} maxWidth="xl" fullWidth>
            <DialogContent sx={{ p: 0 }}>
              <img src={current.url} alt={t(`experiential.distSteps.${current.id}`)} className="exp-present-img" />
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
